import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Composer } from '@/components/ai/composer';
import { ContextBar, contextHint, type AiContextValue } from '@/components/ai/context-selector';
import { AvailabilityNotice, SuggestedPrompts } from '@/components/ai/prompts';
import { WorkspaceHeader } from '@/components/ai/workspace';
import { Button } from '@/components/ui/button';
import { handOff } from '@/lib/ai/handoff';
import { aiKeys, aiSettingsQuery, createConversation } from '@/lib/ai/queries';
import { errorMessage } from '@/lib/forms';
import { useAuthenticatedSession } from '@/lib/session';

export const Route = createFileRoute('/_app/ai/')({ component: NewConversation });

/** A conversation that doesn't exist yet: it is created with the context on the first send. */
function NewConversation() {
  const { user } = useAuthenticatedSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const settings = useQuery(aiSettingsQuery);
  const [context, setContext] = useState<AiContextValue>({ servers: [], infrastructure: false });

  const start = useMutation({
    mutationFn: (content: string) =>
      createConversation({
        servers: context.servers.map((s) => s.identifier),
        infrastructure: context.infrastructure,
      }).then((c) => ({ conversation: c, content })),
    onSuccess: async ({ conversation, content }) => {
      handOff(conversation.id, content);
      void queryClient.invalidateQueries({ queryKey: aiKeys.conversations });
      await navigate({
        to: '/ai/$conversationId',
        params: { conversationId: conversation.id },
      });
    },
  });

  return (
    <>
      <WorkspaceHeader
        title="New conversation"
        status={null}
        context={<ContextBar value={context} isAdmin={user.isAdmin} onChange={setContext} />}
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-6">
        <SuggestedPrompts onPick={(p) => start.mutate(p)} disabled={start.isPending} />
      </div>
      <div className="px-4 pb-4 sm:px-6">
        <AvailabilityNotice settings={settings.data} />
        {start.isError && (
          <p
            role="alert"
            className="text-destructive mx-auto mb-2 flex max-w-3xl flex-wrap items-center gap-2 text-sm"
          >
            Couldn’t start the conversation: {errorMessage(start.error)}
            <Button size="xs" variant="outline" onClick={() => start.mutate(start.variables)}>
              Try again
            </Button>
          </p>
        )}
        <div className="mx-auto max-w-3xl">
          <Composer
            onSend={(c) => start.mutate(c)}
            streaming={false}
            disabled={start.isPending}
            autoFocus
            hint={contextHint(context)}
          />
        </div>
      </div>
    </>
  );
}
